import { useEffect, useMemo, useRef, useState } from "react";
import { fetchProducts } from "./fetchProducts";
import Filters from "./Filters";
import Product from "../../container/Product/Product";

const FilteredProducts = () => {
  const loaderRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    category: "",
    minPrice: "",
    maxPrice: "",
    sort: "",
    limit: 12,
  });

  // stable key so we can reset properly
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const loadProducts = async ({ reset = false } = {}) => {
    if (loading) return;
    if (!hasMore && !reset) return;

    try {
      setLoading(true);

      const nextPage = reset ? 1 : page;

      const data = await fetchProducts({
        ...filters,
        page: nextPage,
      });

      setProducts((prev) => (reset ? data.products : [...prev, ...data.products]));
      setHasMore(nextPage < (data.totalPages || 1));
    } finally {
      setLoading(false);
    }
  };

  // initial + page change
  useEffect(() => {
    loadProducts({ reset: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // reset when filters change
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
    loadProducts({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Intersection Observer (more reliable on mobile)
  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loading) {
          setPage((prev) => prev + 1);
        }
      },
      {
        threshold: 0,            // ✅ works better across devices
        rootMargin: "300px 0px", // ✅ preload before user hits bottom
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  return (
    <div className="w-full min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Filters */}
          <div className="w-full lg:w-[280px] shrink-0">
            <Filters filters={filters} setFilters={setFilters} />
          </div>

          {/* Products */}
          <div className="flex-1 min-w-0">
            {/* IMPORTANT:
              Your Product component currently has min-h-screen + page padding.
              If you keep that, this page will look "nested".
              Best is: update Product to NOT wrap in min-h-screen when used inside pages.
            */}
            <Product products={products} />

            {/* Loader / End */}
            <div ref={loaderRef} className="py-8 flex justify-center">
              {loading && (
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <span className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
                  Loading...
                </div>
              )}

              {!hasMore && !loading && (
                <div className="text-center">
                  <p className="inline-flex items-center justify-center px-5 py-2 rounded-xl border border-gray-200 bg-white text-red font-semibold">
                    No more products
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilteredProducts;
